import './section-content.scss';

import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.js';
import { ActionCardSkeleton } from '@sola/ark-core/molecules/action-card/action-card.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@sola/ark-core/molecules/tooltip/tooltip.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';

type SectionContentHeadingProps =
  | {
      isLoadingHeading: true;
      heading?: never;
      subheading?: never;
      tooltipContent?: never;
    }
  | {
      isLoadingHeading?: false;
      heading: string;
      subheading?: string | React.ReactNode;
      tooltipContent?: string;
    };

type SectionContentContentProps =
  | {
      isLoadingContent: true;
      children?: React.ReactNode;
    }
  | {
      isLoadingContent?: false;
      children: React.ReactNode;
    };

export type SectionContentProps = SectionContentHeadingProps &
  SectionContentContentProps;

/**
 * Section for Card Lists.
 * In the future, we may want to make this more composable depending on our needs.
 */
export const SectionContent: React.FC<SectionContentProps> = ({
  children,
  heading,
  tooltipContent,
  subheading,
  isLoadingHeading,
  isLoadingContent,
}) => {
  return (
    <section className="SectionContent max-w-md lg:flex lg:items-start lg:flex-row lg:justify-between lg:gap-comfy-xl">
      <div className="SectionContent__labels mb-comfy-xl lg:mb-none shrink-0">
        {!isLoadingHeading && (
          <>
            <div className="flex items-center label-potent label-md gap-snug-lg label-neutral-mild">
              <h2 className="label-potent label-md label-neutral-mild">
                {heading}
              </h2>

              {tooltipContent && (
                <Tooltip>
                  <TooltipTrigger>
                    <button
                      type="button"
                      className="SectionContent__tticon flex items-center justify-center"
                      aria-label="Additional information"
                      onClick={(e) => {
                        e.preventDefault();
                      }}
                    >
                      <ArkIcon iconName="info-circle" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    className="label-md lg:label-lg"
                    alignOffset={-12}
                    onPointerDownOutside={(e) => {
                      e.preventDefault();
                    }}
                  >
                    {tooltipContent}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            {subheading && (
              <span className="label-moderate label-lg label-neutral-amplified">
                {subheading}
              </span>
            )}
          </>
        )}

        {isLoadingHeading && <Skeleton height={50} />}
      </div>
      <div className="SectionContent__content w-full flex flex-col gap-comfy-xs">
        {isLoadingContent ? <ActionCardSkeleton /> : children}
      </div>
    </section>
  );
};
